import {StyleSheet, Text, TouchableOpacity, View} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {useLingui} from '@lingui/react/macro'

import {SLIDE_ACCENT, SLIDE_FEATHER_ICON} from '#/lib/slideTokens'
import {type SlideEditorProps} from '#/components/capsules/play/types'
import {StudioTextField} from '#/components/studio/StudioTextField'
import {readableAccent, type StudioChrome} from '#/themes-studio/chrome'
import {useStudioChrome, useStudioStyles} from '#/themes-studio/useChrome'
import {SLIDE_TYPE_META} from '#/types/capsule'
import {WordClickEditor} from './WordClickEditor'

export function DragWordsSlideEditor({
  slide,
  onChange,
  primaryColor: _primaryColor,
  onPreview,
}: SlideEditorProps) {
  const ss = useStyles()
  const chrome = useStudioChrome()
  const {t: l} = useLingui()
  const accent = SLIDE_ACCENT.drag_words
  // Ink, not fill: the taxonomy's colour tuned to read on the studio surface it
  // is written on (see `readableAccent`). Tints stay on the raw hue.
  const accentInk = readableAccent(accent, chrome)
  const meta = SLIDE_TYPE_META.drag_words
  const slideIcon = SLIDE_FEATHER_ICON.drag_words ?? 'help-circle'

  return (
    <View style={ss.root}>
      <View style={ss.header}>
        {/* Host-provided header (e.g. FlashBook studio) replaces this row */}
        <View style={[ss.topRow, onPreview ? null : {display: 'none'}]}>
          <View
            style={[
              ss.typeBadge,
              {backgroundColor: accent + '22', borderColor: accent + '44'},
            ]}>
            <Feather name={slideIcon} size={12} color={accentInk} />
            <Text style={[ss.typeBadgeText, {color: accentInk}]}>
              {meta?.label ?? slide.type}
            </Text>
          </View>
          <TouchableOpacity
            accessibilityRole="button"
            onPress={onPreview}
            hitSlop={6}
            style={[
              ss.previewBtn,
              {backgroundColor: accent + '18', borderColor: accent + '55'},
            ]}>
            <Feather name="play-circle" size={13} color={accentInk} />
            <Text
              style={[
                ss.previewBtnText,
                {color: accentInk},
              ]}>{l`Preview`}</Text>
          </TouchableOpacity>
        </View>
        <StudioTextField
          accessibilityLabel="Text input field"
          accessibilityHint={undefined}
          style={ss.question}
          value={slide.question ?? ''}
          onChangeText={v => onChange({question: v})}
          placeholder={l`Écris ta question ici…`}
          placeholderTextColor={chrome.textFaint}
        />
      </View>
      <WordClickEditor
        slide={slide}
        primaryColor={accent}
        onUpdate={onChange}
        footer={
          <View style={ss.explanationWrap}>
            <Text style={ss.explanationLabel}>
              {l`EXPLICATION (optionnelle)`.toUpperCase()}
            </Text>
            <View style={ss.explanationCard}>
              <StudioTextField
                accessibilityLabel="Text input field"
                accessibilityHint={undefined}
                style={ss.explanationInput}
                value={slide.explanation ?? ''}
                onChangeText={v => onChange({explanation: v})}
                placeholder={l`Explication affichée après la réponse…`}
                placeholderTextColor={chrome.textFaint}
                textAlignVertical="top"
              />
            </View>
          </View>
        }
      />
    </View>
  )
}

const makeStyles = (chrome: StudioChrome) =>
  StyleSheet.create({
    root: {flex: 1, backgroundColor: chrome.frame},
    header: {padding: 20, gap: 12},
    topRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    typeBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      alignSelf: 'flex-start',
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
    },
    typeBadgeText: {fontSize: 11, fontWeight: '700'},
    previewBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 20,
      borderWidth: 1,
    },
    previewBtnText: {fontSize: 11, fontWeight: '700'},
    question: {
      color: chrome.text,
      fontSize: 20,
      fontWeight: '700',
      lineHeight: 28,
      paddingVertical: 0,
    },
    explanationWrap: {gap: 6},
    explanationLabel: {
      color: chrome.textFaint,
      fontSize: 10,
      fontWeight: '700',
      letterSpacing: 0.9,
    },
    explanationCard: {
      backgroundColor: chrome.fill,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: chrome.borderStrong,
      paddingHorizontal: 14,
      paddingVertical: 10,
    },
    explanationInput: {
      color: chrome.textMuted,
      fontSize: 13,
      lineHeight: 18,
      paddingVertical: 0,
    },
  })

/** The editor's styles for the appearance the app is in. */
const useStyles = () => useStudioStyles(makeStyles)
